import { useEffect, useState } from 'react';
import { Browsing, QsNames } from '@src/types/Browsing';
import { ServiceName, Services } from '@src/types/Services';
import { MediaView, ResGetMedia } from 'common';
import { useLocation } from 'react-router';
import { IPopupsService } from '@src/types/Popups';
import { useMediaFindLibrary, useMediaFindFacet } from '@src/queries/media/useQueryMediaFind';
import { XclusionUtility } from '@src/fragments/Filters/managers/XclusionUtility';
import { pathLocationToString, setQsParam } from '@src/utils/browsing';
import { Discover } from './Discover';
import {
  FacetState,
  InitialLibraryState,
  FilterBuiltState,
} from '@src/pages/Discover/components/Atoms/Atoms';
import { useSetAtom } from 'jotai';
import { useQueryMediaFindAll } from '@src/queries/media/useQueryMediaFindAll';
import { useMediaConstraints } from '@src/hooks/useMediaConstraints';
import { useFiltersParams } from '@src/hooks/useFiltersParams';

export const TYPING_DEBOUNCE = 350;

interface Props {
  services: Services;
}

export default function DiscoverController(props: Props) {
  const [isFetchingStories, setIsFetchingStories] = useState(false);
  const { filters } = useFiltersParams();

  const {
    isExactSearch,
    mediaConstraints,
    filterBuilt,
    commonStore: stateForQueries,
  } = useMediaConstraints(filters);
  const setFacetsState = useSetAtom(FacetState);

  // Create states of childrens
  const [filtersOpened, setFiltersOpened] = useState(true);
  const [mediaSelection, setMediaSelection] = useState<MediaView[]>([]);
  const setInitialLibraryStat = useSetAtom(InitialLibraryState);
  const [searchInput, setSearchInput] = useState('');

  const [selectAllActive, setSelectAllActive] = useState(false);

  // Prepare required services
  const [services] = useState(() => ({
    browsing: props.services.get<Browsing>(ServiceName.Browsing),
    popups: props.services.get<IPopupsService>(ServiceName.Popups),
  }));

  // We built the filter here to reuse it in CreateCollection
  const setFilterBuilt = useSetAtom(FilterBuiltState);

  useEffect(() => {
    setFilterBuilt(filterBuilt);
  }, [filterBuilt, setFilterBuilt]);


  // Media queries
  const {
    data: resLibrary,
    isLoading: isLoadingLibrary,
    isFetching: isFetchingLibrary,
    isFetchingNextPage: isFetchingMoreLibrary,
    hasNextPage: canFetchMoreLibrary,
    fetchNextPage: fetchMoreLibrary,
  } = useMediaFindLibrary(
    {
      ...mediaConstraints,
      isExactSearch: false,
      withStories: isFetchingStories,
    },
    // we only want total if it's not the current mode
    isExactSearch ? 0 : undefined
  );
  const {
    data: resLibraryExact,
    isLoading: isLoadingLibraryExact,
    isFetching: isFetchingLibraryExact,
    isFetchingNextPage: isFetchingMoreLibraryExact,
    hasNextPage: canFetchMoreLibraryExact,
    fetchNextPage: fetchMoreLibraryExact,
  } = useMediaFindLibrary(
    {
      ...mediaConstraints,
      isExactSearch: true,
    },
    // we only want total if it's not the current mode
    isExactSearch ? undefined : 0
  );
  const {
    data: allMedia,
    refetch: fetchAllMedia,
    isFetching: isFetchingAll,
  } = useQueryMediaFindAll(
    {
      ...mediaConstraints,
      isExactSearch: true,
    }
  );

  const resLibraryToDisplay = isExactSearch ? resLibraryExact : resLibrary;
  const library: MediaView[] = resLibraryToDisplay?.pages.flatMap((r) => r.media) || [];
  const shoppableUGCs: number = library.filter((media) => media.hasActiveExactProducts || media.hasActiveSimilarProducts).length;
  const canFetchMore = isExactSearch ? canFetchMoreLibraryExact : canFetchMoreLibrary;
  const fetchMore = isExactSearch ? fetchMoreLibraryExact : fetchMoreLibrary;
  const isFetchingMore = isFetchingMoreLibrary || isFetchingMoreLibraryExact;
  const libraryFetching = isFetchingLibrary || isFetchingLibraryExact;
  let libraryLoaded = !isLoadingLibrary && !isLoadingLibraryExact;
  if (isFetchingMore) {
    // prevent hiding the lib when scrolling
    libraryLoaded = true;
  }

  useEffect(() => {
    setInitialLibraryStat({
      total: getTotalMedia(resLibraryToDisplay?.pages),
      shoppableUGCs,
      totalEstimated: getTotalMedia(resLibrary?.pages),
      totalExact: getTotalMedia(resLibraryExact?.pages),
    });
  }, [resLibraryExact, resLibrary]);

  // Filters queries
  // Wait for the library before fetching facets
  const enableFacet = !(isFetchingMore || libraryFetching);

  useEffect(() => {
    setFacetsState({
      typingDebounce: TYPING_DEBOUNCE,
      enableFacet: enableFacet,
      mediaConstraints: mediaConstraints,
    });
  }, [enableFacet, mediaConstraints, setFacetsState]);

  // Search query
  const { data: resSearch, isLoading: isLoadingSearch } = useMediaFindFacet(
    mediaConstraints,
    XclusionUtility.getRawKey(searchInput),
    ['hashtags', 'labels'],
    TYPING_DEBOUNCE,
    enableFacet
  );

  // Get current location for routing purposes
  const location = useLocation();

  const handleSelectAll = async () => {
    if (mediaConstraints.blacklistedCreators === undefined) {
      return;
    }

    try {
      const result = await fetchAllMedia();
      if (result.data) {
        setMediaSelection(result.data);
        setSelectAllActive(true);
      }
    } catch (error) {
      console.error('Error fetching all media:', error);
    }
  };

  if (!stateForQueries.user) {
    return null;
  }
  return (
    <Discover
      isExactMode={isExactSearch}
      qsValues={filters}
      pathLocation={location}
      services={services}
      searchState={{
        input: searchInput,
        setInput: setSearchInput,
        isLoading: isLoadingSearch,
        result: resSearch?.pages[0],
      }}
      commonStore={stateForQueries}
      filtersOpened={filtersOpened}
      mediaSelection={mediaSelection}
      setMediaSelection={setMediaSelection}
      setFiltersOpened={setFiltersOpened}
      setExactMode={setExactMode}
      library={library}
      displayFetchingLoader={libraryLoaded && libraryFetching}
      libraryLoaded={libraryLoaded}
      noMoreMedia={!canFetchMore}
      loadMoreMedia={fetchMore}
      totalMedia={getTotalMedia(resLibrary?.pages)}
      totalExactMedia={getTotalMedia(resLibraryExact?.pages)}
      handleSelectAll={handleSelectAll}
      selectAllActive={selectAllActive}
      setSelectAllActive={setSelectAllActive}
      isFetchingAll={isFetchingAll}
      isFetchingStories={isFetchingStories}
      setIsFetchingStories={setIsFetchingStories}
    />
  );

  function setExactMode(mode: boolean) {
    services.browsing.goToPage(
      pathLocationToString(
        setQsParam(location, [
          {
            field: QsNames.discoverExactMode,
            val: mode ? 'true' : undefined,
          },
        ])
      )
    );
  }

}

function getTotalMedia(res: ResGetMedia[] | undefined): number {
  if (!res) {
    return 0;
  }
  const [r] = res;
  return r?.total || 0;
}
